import { useEffect, useRef, useState } from "react";
import { Percent, Truck, Cigarette, Leaf, Gift, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = {
  percent: { Icon: Percent, label: "10% OFF", color: "#a06d34" },
  percent15: { Icon: Percent, label: "15% OFF", color: "#8a5a24" },
  truck: { Icon: Truck, label: "FREE SHIP", color: "#5b8fb0" },
  preroll: { Icon: Cigarette, label: "PREROLL", color: "#c99a63" },
  flower: { Icon: Leaf, label: "FLOWER", color: "#6f9e5f" },
  gift: { Icon: Gift, label: "BOGO", color: "#c98a8a" },
};
const ORDER = ["percent", "truck", "preroll", "percent15", "flower", "gift"];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const rand = () => ORDER[Math.floor(Math.random() * ORDER.length)];

function Reel({ symbol, spinning }) {
  const meta = ICONS[symbol] || ICONS.percent;
  const { Icon } = meta;
  return (
    <div
      className="flex h-24 w-24 flex-col items-center justify-center gap-1 border border-black/10 bg-[#faf7f1] shadow-inner sm:h-28 sm:w-28"
      style={{ boxShadow: "inset 0 4px 14px rgba(43,37,32,0.08)" }}
    >
      <Icon
        className={spinning ? "opacity-60 blur-[1px]" : "transition-all"}
        style={{ color: meta.color, width: 40, height: 40 }}
        strokeWidth={1.4}
      />
      <span className="text-[9px] font-bold uppercase tracking-widest text-neutral-500">
        {meta.label}
      </span>
    </div>
  );
}

export default function SlotMachine({ sessionId }) {
  const [info, setInfo] = useState(null);
  const [reels, setReels] = useState(["percent", "truck", "preroll"]);
  const [spinning, setSpinning] = useState(false);
  const [prize, setPrize] = useState(null);
  const [won, setWon] = useState([]);
  const ivRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    api
      .get(`/slot/${sessionId}`)
      .then(({ data }) => setInfo(data))
      .catch(() => setInfo({ paid: false, spins_remaining: 0 }));
  }, [sessionId]);

  useEffect(() => () => clearInterval(ivRef.current), []);

  const remaining = info?.spins_remaining ?? 0;

  const spin = async () => {
    if (spinning || remaining <= 0) return;
    setSpinning(true);
    setPrize(null);
    ivRef.current = setInterval(() => setReels([rand(), rand(), rand()]), 90);
    try {
      const { data } = await api.post("/slot/spin", { session_id: sessionId });
      await sleep(1300);
      clearInterval(ivRef.current);
      setReels([data.reels[0], rand(), rand()]);
      await sleep(300);
      setReels([data.reels[0], data.reels[1], rand()]);
      await sleep(300);
      setReels(data.reels);
      await sleep(200);
      setPrize(data.prize);
      setWon((w) => [...w, data.prize]);
      setInfo((i) => ({ ...i, spins_remaining: data.spins_remaining }));
    } catch (e) {
      clearInterval(ivRef.current);
    } finally {
      setSpinning(false);
    }
  };

  if (!info || !info.paid || (info.spins_total ?? 0) === 0) return null;

  return (
    <div
      data-testid="slot-machine"
      className="mx-auto mt-12 w-full max-w-md border border-[#c99a63]/40 bg-white p-8 text-center shadow-[0_20px_60px_rgba(43,37,32,0.12)]"
    >
      <div className="mb-1 flex items-center justify-center gap-2 text-[#a06d34]">
        <Sparkles className="h-4 w-4" strokeWidth={1.5} />
        <p className="overline">Winner's Circle</p>
        <Sparkles className="h-4 w-4" strokeWidth={1.5} />
      </div>
      <h2 className="mb-1 text-3xl font-black text-[#2b2520]">Spin to Win</h2>
      <p className="mb-6 text-sm text-neutral-600">
        Thanks for your order! Every spin wins a reward you'll get on your{" "}
        <span className="font-semibold text-[#2b2520]">next purchase</span> — the more you spend, the more spins you earn.
      </p>

      <div className="mb-5 flex items-center justify-center gap-3 rounded-none bg-[#2b2520] p-4">
        {reels.map((s, i) => (
          <Reel key={i} symbol={s} spinning={spinning} />
        ))}
      </div>

      {prize && !spinning && (
        <div
          data-testid="slot-prize"
          className="mb-5 border border-[#2e7d32]/40 bg-[#2e7d32]/10 p-4 text-[#2e7d32]"
        >
          <p className="text-xs uppercase tracking-widest">You won</p>
          <p className="text-2xl font-black">{prize.label}</p>
          <p className="mt-1 text-xs text-[#2b2520]/70">
            It's saved to your account and will be waiting at your next checkout.
          </p>
        </div>
      )}

      <p data-testid="spins-remaining" className="mb-3 text-xs font-semibold uppercase tracking-widest text-neutral-500">
        {remaining > 0 ? `${remaining} spin${remaining > 1 ? "s" : ""} remaining` : "No spins left"}
      </p>

      <button
        data-testid="slot-spin-btn"
        onClick={spin}
        disabled={spinning || remaining <= 0}
        className="flex w-full items-center justify-center gap-2 bg-[#c99a63] py-4 text-sm font-bold uppercase tracking-widest text-[#2b2520] transition-colors hover:bg-[#bb8850] disabled:cursor-not-allowed disabled:opacity-50"
      >
        {spinning ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Spinning…
          </>
        ) : remaining > 0 ? (
          "Pull the lever"
        ) : (
          "All spins used"
        )}
      </button>

      {won.length > 0 && (
        <div className="mt-6 border-t border-black/10 pt-4 text-left" data-testid="slot-won-list">
          <p className="mb-2 text-xs uppercase tracking-widest text-neutral-500">Your rewards</p>
          <ul className="space-y-1 text-sm text-[#2b2520]">
            {won.map((p, i) => (
              <li key={i} className="flex items-center gap-2">
                <Gift className="h-3.5 w-3.5 text-[#a06d34]" /> {p.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
